import { AxisBottom, AxisLeft } from '@visx/axis';
import { localPoint } from '@visx/event';
import { Grid } from '@visx/grid';
import { Group } from '@visx/group';
import { LegendOrdinal } from '@visx/legend';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { BarStack } from '@visx/shape';
import { defaultStyles, useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { FC, useMemo } from 'react';

type DataItem = { x: string; y: number };
type CategoryData = { [key: string]: DataItem[] };
type TooltipData = {
    key: string;
    bar: any;
    index: number;
    x: number;
    y: number;
    width: number;
    height: number;
    color: string;
};

export type StackedBarChartProps = {
    data: CategoryData;
    width: number;
    height: number;
    margin?: { top: number; right: number; bottom: number; left: number };
    resize?: any;
};

const defaultMargin = { top: 20, right: 0, bottom: 40, left: 40 };

const colors = {
    information: '#7dd2ff',
    low: '#6EB8FF',
    medium: '#FFB020',
    high: '#D14343',
    critical: '#7B0000',
};

const tooltipStyles = {
    ...defaultStyles,
    backgroundColor: '#333',
    color: '#fff',
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '12px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
};

const StackedBarChart: FC<StackedBarChartProps> = ({ data, width, height, margin = defaultMargin }) => {
    const keys = useMemo(() => Object.keys(data), [data]);

    const totals = useMemo(() => {
        return data[keys[0]].map((_, i) => keys.reduce((acc, key) => acc + data[key][i].y, 0));
    }, [data, keys]);

    const xScale = scaleBand<string>({
        domain: data[keys[0]].map((d) => d.x),
        padding: 0.2,
    });

    const yScale = scaleLinear<number>({
        domain: [0, Math.max(...totals)],
        nice: true,
    });

    const colorScale = scaleOrdinal<string, string>({
        domain: keys,
        range: keys.map((key) =>
            //@ts-ignore
            colors[key]),
    });

    const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
        useTooltip<TooltipData>();
    const { containerRef, TooltipInPortal } = useTooltipInPortal({ scroll: true });

    if (width < 10) return null;

    const xMax = width - margin.left - margin.right;
    const yMax = height - margin.top - margin.bottom;
    xScale.rangeRound([0, xMax]);
    yScale.range([yMax, 0]);

    let tooltipTimeout: number;

    return (
        <div style={{ position: 'relative', background: '#fafafa', borderRadius: '12px', padding: '8px' }}>
            <svg ref={containerRef} width={width} height={height}>
                <rect x={0} y={0} width={width} height={height} fill="transparent" />
                <Grid
                    top={margin.top}
                    left={margin.left}
                    xScale={xScale}
                    yScale={yScale}
                    stroke="#e0e0e0"
                    strokeDasharray="4 4"
                    width={xMax}
                    height={yMax}
                />
                <Group top={margin.top} left={margin.left}>
                    <BarStack<DataItem, string>
                        data={data[keys[0]].map((_, i) =>
                            keys.reduce(
                                (acc, key) => {
                                    acc[key] = data[key][i].y;
                                    return acc;
                                },
                                { x: data[keys[0]][i].x } as any
                            )
                        )}
                        keys={[...keys].reverse()}
                        x={(d) => d.x}
                        xScale={xScale}
                        yScale={yScale}
                        color={colorScale}
                    >
                        {(barStacks) =>
                            barStacks.map((barStack) =>
                                barStack.bars.map((bar) => {
                                    const isTopBar = barStack.index === 0;
                                    return (
                                        <rect
                                            key={`bar-stack-${barStack.index}-${bar.index}`}
                                            x={bar.x}
                                            y={bar.y}
                                            width={bar.width}
                                            height={bar.height}
                                            rx={isTopBar ? 4 : 0}
                                            ry={isTopBar ? 4 : 0}
                                            fill={bar.color}
                                            style={{ transition: 'all 0.2s ease-in' }}
                                            onMouseLeave={() => {
                                                tooltipTimeout = window.setTimeout(() => hideTooltip(), 300);
                                            }}
                                            onMouseMove={(event) => {
                                                if (tooltipTimeout) clearTimeout(tooltipTimeout);
                                                const coords = localPoint(event);
                                                showTooltip({
                                                    tooltipData: bar,
                                                    tooltipLeft: coords?.x,
                                                    tooltipTop: coords?.y,
                                                });
                                            }}
                                        />
                                    );
                                })
                            )
                        }
                    </BarStack>
                </Group>
                <AxisBottom
                    top={yMax + margin.top}
                    left={margin.left}
                    scale={xScale}
                    stroke="#ccc"
                    tickStroke="#ccc"
                    tickLabelProps={() => ({
                        fill: '#666',
                        fontSize: 12,
                        textAnchor: 'middle',
                        dy: '0.25em',
                    })}
                />
                <AxisLeft
                    scale={yScale}
                    top={margin.top}
                    left={margin.left}
                    stroke="#ccc"
                    tickStroke="#ccc"
                    tickLabelProps={() => ({
                        fill: '#666',
                        fontSize: 12,
                        textAnchor: 'end',
                        dx: '-0.25em',
                        dy: '0.25em',
                    })}
                    //@ts-ignore
                    tickFormat={(value) => Math.round(value)}
                    //@ts-ignore
                    tickValues={yScale.ticks().filter((value) => Number.isInteger(value))}
                />
            </svg>

            <div
                style={{
                    position: 'absolute',
                    top: height - margin.bottom + 40,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '20px',
                    fontSize: '12px',
                    textTransform: "uppercase",
                    fontWeight: 600,
                    color: '#555',
                }}
            >
                <LegendOrdinal
                    scale={colorScale}
                    direction="row"
                    labelMargin="0 1.2em 0 0"
                    legendLabelProps={{
                        size: 8
                    }}
                    shape={({ fill }) => (
                        <div
                            style={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: fill,
                                display: 'inline-block',
                                marginRight: 6,
                            }}
                        />
                    )}
                />
            </div>

            {tooltipOpen && tooltipData && (
                <TooltipInPortal top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
                    <div>
                        <strong>{tooltipData.key}</strong>
                    </div>
                    <div>{tooltipData.bar.data[tooltipData.key]}</div>
                </TooltipInPortal>
            )}
        </div>
    );
};

export default StackedBarChart;